import headerFooterStyles from "@fsa-style/src/stylesheets/fsa-style.scss?inline";
import usdaLogoUrl from "@fsa-style/src/img/usda-logo--white.svg";
import faviconSVGUrl from "@assets/favicon.svg";
import faviconPNGUrl from "@assets/favicon-96x96.png";
import appleTouchImgUrl from "@assets/apple-touch-icon.png";
import faviconUrl from "@assets/favicon.ico";
import webManifestUrl from "@assets/site.webmanifest";

import { loadNav, getQueryArgs } from "@js/nwcc-navbar.js";

import { nwccDomain } from "@js/nwcc-domains.js";

const ASSETS_PATH = "/assets";

export function renderTemplate() {
    // build page
    hideMainContent();
    // addGtag() // this should be uncommented and the addAnalytics removed once migrated to gtag.js
    addAnalytics(); // for the old analytics.js based GTAG
    addIcons();
    loadNav();
}

// functions

export function addGtag() {
    const envObj = import.meta.env;
    if (!location.hostname.split(".").some((env) => ["dev", "uat", "localhost"].includes(env))) {
        const GTAG = "GTM-MGLT27J3";
        const fsaAnalyticsScriptAsync = document.createElement("script");
        fsaAnalyticsScriptAsync.setAttribute("src", `https://www.googletagmanager.com/gtag/js?id=${GTAG}`);
        fsaAnalyticsScriptAsync.setAttribute("async", true);
        document.head.appendChild(fsaAnalyticsScriptAsync);
        const fsaAnalyticsScript = document.createElement("script");
        fsaAnalyticsScript.innerHTML = /*js*/ `
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
            gtag('config', '${GTAG}');`;

        document.head.appendChild(fsaAnalyticsScript);
    } else {
        if (envObj?.MODE === "development" || envObj?.MODE === "test") {
            console.log("Not tracking with Analytics");
        }
    }
}

export function addAnalytics() {
    const envObj = import.meta.env;
    if (!location.hostname.split(".").some((env) => ["dev", "uat", "localhost"].includes(env))) {
        const GTAG = "GTM-MGLT27J3";
        const fsaAnalyticsNoScript = document.createElement("noscript");
        const fsaAnalyticsIframe = document.createElement("iframe");
        fsaAnalyticsIframe.setAttribute("src", `https://www.googletagmanager.com/ns.html?id=${GTAG}`);
        fsaAnalyticsIframe.setAttribute("height", "0");
        fsaAnalyticsIframe.setAttribute("width", "0");
        fsaAnalyticsIframe.setAttribute("style", "display:none;visibility:hidden");
        fsaAnalyticsNoScript.appendChild(fsaAnalyticsIframe);
        const fsaAnalyticsScript = document.createElement("script");
        fsaAnalyticsScript.innerHTML = /*js*/ `
            (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
            new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
            j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
            'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
            })(window,document,'script','dataLayer','${GTAG}');`;

        document.head.appendChild(fsaAnalyticsScript);
        document.body.appendChild(fsaAnalyticsNoScript);
    } else {
        if (envObj?.MODE === "development" || envObj?.MODE === "test") {
            console.log("Not tracking with Analytics");
        }
    }
}

export function hideMainContent() {
    const main = document.getElementById("main-content");
    if (main) {
        main.style.visibility = "hidden";
    }
}

export function findFirstFocusableElement() {
    const focusableElements = document
        .getElementById("main-content")
        ?.querySelectorAll('button, a[href], input, select, textarea, [tabindex]:not([tabindex="-1"])');

    const filteredFocusableElements = focusableElements
        ? [...focusableElements].filter((el) => !el.hasAttribute("disabled") && el.getAttribute("type") !== "hidden")
        : [];

    return filteredFocusableElements[0] || null;
}

export function loadMainNwcc() {
    const main = document.getElementById("main-content");
    main.style.visibility = "visible";
    const shadowDom = document.getElementsByTagName("nwcc-header")[0]?.shadowRoot;
    const splash = shadowDom?.getElementById("loading-splash");
    if (splash) {
        splash.style.display = "none";
    }
}

export function addIcons() {
    const htmlHead = document.head;

    const iconLargeLink = document.createElement("link");
    iconLargeLink.type = "image/png";
    iconLargeLink.sizes = "96x96";
    iconLargeLink.href = faviconPNGUrl;
    iconLargeLink.rel = "icon";

    const iconLink = document.createElement("link");
    iconLink.type = "image/svg+xml";
    iconLink.href = faviconSVGUrl;
    iconLink.rel = "icon";

    const iconShortcutLink = document.createElement("link");
    iconShortcutLink.type = "image/png";
    iconShortcutLink.href = faviconUrl;
    iconShortcutLink.rel = "shortcut icon";

    const iconAppleTouchLink = document.createElement("link");
    iconAppleTouchLink.sizes = "180x180";
    iconAppleTouchLink.href = appleTouchImgUrl;
    iconAppleTouchLink.rel = "apple-touch-icon";

    const iconManifestLink = document.createElement("link");
    iconManifestLink.href = webManifestUrl;
    iconManifestLink.rel = "manifest";

    const titleAppleTouch = document.createElement("meta");
    titleAppleTouch.name = "apple-mobile-web-app-title";
    titleAppleTouch.content = "NWCC Apps";

    htmlHead.appendChild(iconLargeLink);
    htmlHead.appendChild(iconLink);
    htmlHead.appendChild(iconShortcutLink);
    htmlHead.appendChild(iconAppleTouchLink);
    htmlHead.appendChild(iconManifestLink);
    htmlHead.appendChild(titleAppleTouch);
}

/**
 * Find a valid element after the header to scroll to and focus on. Elements will be selected
 * by whichever is found first in the following order:
 * 	1. The first anchor element with the #main-content-override ID/Name.
 * 	2. The first focusable element after the header.
 * 	3. The first h1 element.
 * 	4. The first div with the 'main-content' ID.
 * 	5. Default to the body.
 * @returns Element to scroll to and focus on.
 */
export function getContentToSkipTo() {
    const mainContentOverride = document.querySelector("#main-content-override");
    if (mainContentOverride) return mainContentOverride;

    const firstFocusableElement = findFirstFocusableElement();
    if (firstFocusableElement) return firstFocusableElement;

    const firstHeaderElement = document.querySelector("h1");
    if (firstHeaderElement) return firstHeaderElement;

    const mainContentElement = document.getElementById("main-content");
    if (mainContentElement) return mainContentElement;

    return document.body;
}

export function getSkipButtonStyle() {
    return /*css*/ `
        .skip-to-main-content-link {
            position: absolute;
            left: -9999px;
            z-index: 999;
            padding: 1em;
            background-color: black;
            color: white;
            opacity: 0;
        }
        .skip-to-main-content-link:focus {
            left: 50%;
            top: 2%;
            transform: translateX(-50%);
            opacity: 1;
        }
    `;
}

customElements.define(
    "nwcc-header",
    class extends HTMLElement {
        constructor() {
            super();
            const shadowHead = document.createElement("head");
            const shadowMeta = document.createElement("meta");
            shadowMeta.name = "viewport";
            shadowMeta.content = "width=device-width, initial-scale=1";
            const fsaStyleSheet = new CSSStyleSheet();
            fsaStyleSheet.replaceSync(headerFooterStyles + getSkipButtonStyle());
            shadowHead.appendChild(shadowMeta);
            const header = document.createElement("template");
            const loadingSplash = document.createElement("template");
            header.innerHTML = /*html*/ `
                <header>
                    <button id="skip-content-button" class="skip-to-main-content-link">Skip to Main Content</button>
                    <div class="fsa-tophat fsa-tophat--fullscreen">
                        <div class="fsa-tophat__bd">
                            <div class="fsa-tophat__primary">
                            <span class="fsa-tophat__agency">
                                <a class="fsa-tophat__link" target="_blank" href="//nrcs.usda.gov" title="Click to navigate to the NRCS homepage">
                                <img class="fsa-tophat__agency-logo" src="${usdaLogoUrl}" alt="USDA Logo">
                                <abbr class="fsa-tophat__agency-abbr" title="Natural Resources Conservation Service">NRCS</abbr>
                                <span class="fsa-tophat__agency-fullname">Natural Resources Conservation Service</span>
                                </a>
                            </span>
                            </div>
                            <div class="fsa-tophat__secondary">
                                <span class="fsa-tophat__subagency">
                                    National Water and Climate Center
                                </span>
                            </div>
                        </div>
                    </div>
                    <div class="fsa-header-app fsa-header-app--fullscreen">
                        <div class="fsa-header-app__bd">
                            <div class="fsa-header-app__primary">
                                <a class="fsa-header-app__home-link" href="javascript:window.location.reload()" title="Click to refresh this page">
                                    <span class="fsa-header-app__app-name">
                                    <abbr class="fsa-header-app__app-abbr">
                                        <slot name="app-acronym">[APP ACRONYM]</slot>
                                    </abbr> <!--change-->
                                    <span class="fsa-header-app__app-full">
                                        <slot name="application-long-name">[Application Long Name]</slot>
                                    </span> <!--change-->
                                    </span>
                                </a>
                            </div>
                        </div>
                        <div class="fsa-header-app__secondary">
                            <span class="fsa-header-app__profile"></span>
                        </div>
                    </div>
                    <div class="fsa-nav-global fsa-nav-global--fullscreen">
                        <div class="fsa-nav-global__bd">
                            <ul class="fsa-nav-global__list" id="primary-navigation" aria-label="Primary Navigation">
                                <li class="fsa-nav-global__list-item">
                                    <a class="fsa-nav-global__link" href="https://www.nrcs.usda.gov/conservation-basics/natural-resource-concerns/water/national-water-and-climate-center/" target="_blank">
                                        <span class="fsa-nav-global__text">NWCC Home</span>
                                    </a>
                                </li>
                                <li class="fsa-nav-global__list-item">
                                    <a class="fsa-nav-global__link" href="${nwccDomain}/imap/">
                                        <span class="fsa-nav-global__text">Interactive Map</span>
                                    </a>
                                </li>
                                <li class="fsa-nav-global__list-item fsa-nav-global__list-item--multi-column">
                                    <button
                                        class="fsa-nav-global__link fsa-nav-global__link--has-sub-menu"
                                        type="button"
                                        aria-expanded="false"
                                        aria-controls="site-plots-nav-div"
                                    >
                                        <span class="fsa-nav-global__text" id="site-plots-nav-btn">Site Plots</span>
                                    </button>
                                    <div class="fsa-nav-global__sub-menu" id="site-plots-nav-div" aria-hidden="true">
                                        <div class="fsa-nav-global__sub-menu-bd">
                                            <ul id="site-plots-nav-ul" class="fsa-nav-global__sub-menu-list" aria-labelledby="site-plots-nav-btn"></ul>
                                        </div>
                                    </div>
                                </li>
                                <li class="fsa-nav-global__list-item fsa-nav-global__list-item">
                                    <button
                                        class="fsa-nav-global__link fsa-nav-global__link--has-sub-menu"
                                        type="button"
                                        aria-expanded="false"
                                        aria-controls="site-reports-nav-div"
                                    >
                                        <span class="fsa-nav-global__text" id="site-reports-nav-btn">Site Tools</span>
                                    </button>
                                    <div class="fsa-nav-global__sub-menu" id="site-reports-nav-div" aria-hidden="true">
                                        <div class="fsa-nav-global__sub-menu-bd">
                                            <ul id="site-reports-nav-ul" class="fsa-nav-global__sub-menu-list" aria-labelledby="site-reports-nav-btn"></ul>
                                        </div>
                                    </div>
                                </li>
                                <li class="fsa-nav-global__list-item fsa-nav-global__list-item--multi-column">
                                    <button
                                        class="fsa-nav-global__link fsa-nav-global__link--has-sub-menu"
                                        type="button"
                                        aria-expanded="false"
                                        aria-controls="basin-plots-nav-div"
                                    >
                                        <span class="fsa-nav-global__text" id="basin-plots-nav-btn">Basin Plots</span>
                                    </button>
                                    <div class="fsa-nav-global__sub-menu" id="basin-plots-nav-div" aria-hidden="true">
                                        <div class="fsa-nav-global__sub-menu-bd">
                                            <div class="fsa-nav-global__sub-menu-group">
                                                <h1 class="fsa-nav-global__sub-menu-title">Snow Survey States</h1>
                                                <ul id="basin-plots-nav-ul" class="fsa-nav-global__sub-menu-list" aria-labelledby="basin-plots-nav-btn"></ul>
                                            </div>
                                            <div class="fsa-nav-global__sub-menu-group">
                                                <h1 class="fsa-nav-global__sub-menu-title">Western Hydrologic Regions</h1>
                                                <ul id="west-region-plots-nav-ul" class="fsa-nav-global__sub-menu-list" aria-labelledby="basin-plots-nav-btn"></ul>
                                            </div>
                                            <div class="fsa-nav-global__sub-menu-group">
                                                <h1 class="fsa-nav-global__sub-menu-title">Eastern Hydrologic Regions</h1>
                                                <ul id="east-region-plots-nav-ul" class="fsa-nav-global__sub-menu-list" aria-labelledby="basin-plots-nav-btn"></ul>
                                            </div>
                                        </div>
                                    </div>
                                </li>
                                <li class="fsa-nav-global__list-item fsa-nav-global__list-item">
                                    <button
                                        class="fsa-nav-global__link fsa-nav-global__link--has-sub-menu"
                                        type="button"
                                        aria-expanded="false"
                                        aria-controls="basin-reports-nav-div"
                                    >
                                        <span class="fsa-nav-global__text" id="basin-reports-nav-btn">Basin Tools</span>
                                    </button>
                                    <div class="fsa-nav-global__sub-menu" id="basin-reports-nav-div" aria-hidden="true">
                                        <div class="fsa-nav-global__sub-menu-bd">
                                            <ul id="basin-reports-nav-ul" class="fsa-nav-global__sub-menu-list" aria-labelledby="basin-reports-nav-btn"></ul>
                                        </div>
                                    </div>
                                </li>
                                <li class="fsa-nav-global__list-item fsa-nav-global__list-item--multi-column">
                                    <button
                                        class="fsa-nav-global__link fsa-nav-global__link--has-sub-menu"
                                        type="button"
                                        aria-expanded="false"
                                        aria-controls="forecast-tools-nav-div"
                                        id="forecast-tools-nav-btn"
                                    >
                                        <span class="fsa-nav-global__text">Water Supply</span>
                                    </button>
                                    <div class="fsa-nav-global__sub-menu" id="forecast-tools-nav-div" aria-hidden="true">
                                        <div class="fsa-nav-global__sub-menu-bd">
                                            <div class="fsa-nav-global__sub-menu-group">
                                                <h1 class="fsa-nav-global__sub-menu-title">Basin Forecast Plots</h1>
                                                <ul id="forecast-plots-nav-ul" class="fsa-nav-global__sub-menu-list" aria-labelledby="forecast-tools-nav-btn"></ul>
                                            </div>
                                            <div class="fsa-nav-global__sub-menu-group">
                                                <h1 class="fsa-nav-global__sub-menu-title">Forecast Evolution Plots</h1>
                                                <ul id="forecast-evo-plots-nav-ul" class="fsa-nav-global__sub-menu-list" aria-labelledby="forecast-tools-nav-btn"></ul>
                                            </div>
                                        </div>
                                    </div>
                                </li>
                                <li class="fsa-nav-global__list-item fsa-nav-global__list-item">
                                    <button
                                        class="fsa-nav-global__link fsa-nav-global__link--has-sub-menu"
                                        type="button"
                                        aria-expanded="false"
                                        aria-controls="web-services-nav-div"
                                    >
                                        <span class="fsa-nav-global__text" id="web-services-nav-btn">Webservices</span>
                                    </button>
                                    <div class="fsa-nav-global__sub-menu" id="web-services-nav-div" aria-hidden="true">
                                        <div class="fsa-nav-global__sub-menu-bd">
                                            <ul id="web-services-nav-ul" class="fsa-nav-global__sub-menu-list" aria-labelledby="web-services-nav-btn"></ul>
                                        </div>
                                    </div>
                                </li>
                                <li class="fsa-nav-global__list-item">
                                    <a class="fsa-nav-global__link" href="https://www.nrcs.usda.gov/contact/national-center-contacts/national-water-and-climate-center-contact-information" target="_blank">
                                        <span class="fsa-nav-global__text">Contact Us</span>
                                    </a>
                                </li>
                            </ul>
                        </div>
                    </div>`;

            loadingSplash.innerHTML = /*html*/ `       
                <div class="fsa-splash" id="loading-splash">
                    <div class="fsa-splash__bd">
                        <div class="fsa-splash__item">
                            <div class="fsa-splash__progress">
                                <div class="fsa-progress fsa-progress--indeterminate" aria-live="polite">
                                    <div class="fsa-progress fsa-progress--indeterminate" aria-live="polite">
                                        <div class="fsa-progress__details fsa-sr-only">
                                            <div class="fsa-progress__label">Application is loading</div>
                                        </div>
                                        <div class="fsa-progress__bar" aria-hidden="true">
                                            <div class="fsa-progress__primary"></div>
                                            <div class="fsa-progress__secondary"></div>
                                        </div>
                                    </div>
                                </div>
                                <div class="fsa-splash__title">
                                    <span class="fsa-splash__title-abbr"><slot name='splash-short-title'>[Splash Short Title]</slot></span>
                                    <span class="fsa-splash__title-name"><slot name='splash-long-title'>[Splash Long Title]</slot></span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </header>`;

            const shadowRoot = this.attachShadow({ mode: "open" });
            shadowRoot.adoptedStyleSheets = [fsaStyleSheet];
            const headerContent = header.content;
            const splashContent = loadingSplash.content;
            shadowRoot.appendChild(shadowHead);
            shadowRoot.appendChild(headerContent.cloneNode(true));
            shadowRoot.appendChild(splashContent.cloneNode(true));

            const skipButton = shadowRoot.getElementById("skip-content-button");
            skipButton.addEventListener("click", function () {
                const content = getContentToSkipTo();

                content.scrollIntoView({ behavior: "smooth" });
                content.focus(); // Focus content if possible
                skipButton.blur(); // Unfocus the skip content button
            });
            renderTemplate();
        }
    },
);

customElements.define(
    "nwcc-footer",
    class extends HTMLElement {
        constructor() {
            super();
            const fsaStyleSheet = new CSSStyleSheet();
            fsaStyleSheet.replaceSync(headerFooterStyles);

            const footer = document.createElement("template");
            footer.innerHTML = /*html*/ `           
                <footer class="fsa-footer fsa-footer--big" role="contentinfo">
                    <div class="fsa-footer__bd">
                        <div class="fsa-grid">
                            <div class="fsa-footer__primary fsa-grid__1/1 fsa-grid__9/12@l">
                                <div class="fsa-grid">
                                    <div class="fsa-grid__1/1 fsa-grid__1/3@m">
                                        <h1 class="fsa-footer__cat-header">Important Links</h1>
                                        <ul class="fsa-footer__list">
                                            <li class="fsa-footer__list-item">
                                                <a href="https://www.nrcs.usda.gov/conservation-basics/natural-resource-concerns/water/national-water-and-climate-center/">National Water and Climate Center</a>
                                            </li>
                                            <li class="fsa-footer__list-item">
                                                <a href="https://www.nrcs.usda.gov/">NRCS</a>
                                            </li>
                                            <li class="fsa-footer__list-item">
                                                <a href="https://www.usda.gov/">USDA</a>
                                            </li>
                                        </ul>
                                    </div>
                                    <div class="fsa-grid__1/1 fsa-grid__1/3@m">
                                        <h1 class="fsa-footer__cat-header">Agency Guidelines</h1>
                                        <ul class="fsa-footer__list">
                                            <li class="fsa-footer__list-item">
                                                <a href="https://www.usda.gov/accessibility-statement">Accessibility</a>
                                            </li>
                                            <li class="fsa-footer__list-item">
                                                <a href="https://www.nrcs.usda.gov/foia/">FOIA</a>
                                            </li>
                                            <li class="fsa-footer__list-item">
                                                <a href="https://www.usda.gov/policies-and-links">Privacy Policy</a>
                                            </li>
                                            <li class="fsa-footer__list-item">
                                                <a href="https://www.nrcs.usda.gov/non-discrimination/">Non-Discrimination Statement</a>
                                            </li>
                                        </ul>
                                    </div>
                                    <div class="fsa-grid__1/1 fsa-grid__1/3@m">
                                        <h1 class="fsa-footer__cat-header">Resources</h1>
                                        <ul class="fsa-footer__list">
                                            <li class="fsa-footer__list-item">
                                                <a href="https://www.usda.gov/glossary/">Glossary</a>
                                            </li>
                                            <li class="fsa-footer__list-item">
                                                <a href="https://www.usda.gov/nofear/">No Fear Act Data</a>
                                            </li>
                                            <li class="fsa-footer__list-item">
                                                <a href="https://www.usda.gov/open/">Open Government</a>
                                            </li>
                                            <li class="fsa-footer__list-item">
                                                <a href="https://www.usda.gov/plain-writing/">Plain Writing</a>
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                            <div class="fsa-footer__secondary fsa-grid__1/1 fsa-grid__3/12@l">
                                <ul class="fsa-footer__list fsa-footer__secondary-list">
                                    <li class="fsa-footer__list-item fsa-footer__secondary-list-item">
                                        <h1 class="fsa-footer__cat-header">AskUSDA</h1>
                                        <a class="fsa-btn fsa-footer__btn" href="https://ask.usda.gov/s/">Ask a Question</a>
                                    </li>
                                    <li class="fsa-footer__list-item fsa-footer__secondary-list-item">
                                        <svg
                                            class="fsa-icon fsa-icon--size-4"
                                            aria-hidden="true"
                                            focusable="false"
                                            role="img"
                                            fill="#494440"
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                        >
                                            <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"></path>
                                        </svg>
                                        <a href="tel:8336638732" aria-label="AskUSDA Telephone Number: 1-833-663-8732" target="_blank">
                                            1-833-663-8732
                                        </a>
                                    </li>
                                    <li class="fsa-footer__list-item fsa-footer__secondary-list-item">
                                        <svg
                                            class="fsa-icon fsa-icon--size-4"
                                            aria-hidden="true"
                                            focusable="false"
                                            role="img"
                                            fill="#494440"
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                        >
                                            <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"></path>
                                        </svg>
                                        <a href="mailto:askusda@usda.gov" target="_blank">
                                            askusda@usda.gov
                                        </a>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </footer>`;

            const footerContent = footer.content;
            const shadowRoot = this.attachShadow({ mode: "open" });
            shadowRoot.adoptedStyleSheets = [fsaStyleSheet];
            shadowRoot.appendChild(footerContent.cloneNode(true));
        }
    },
);

export function setArgDetailsData(pageArgs) {
    let argDetailsElement = document.querySelector("arg-details");
    if (
        !pageArgs
            .map((arg) => {
                return arg.argName.toLowerCase();
            })
            .includes("internal")
    ) {
        pageArgs.push({
            argName: "internal",
            argDesc: "Displays any NWCC internal arguments that can be passed to this page (if any exist).",
            argExample: "true",
            argOptions: ["true", "false"],
            argDefault: "false",
            argInternal: true,
        });
    }
    let argData = JSON.stringify(pageArgs);
    argDetailsElement.setAttribute("data-args", argData);
    argDetailsElement.addArguments();
}

customElements.define(
    "arg-details",
    class extends HTMLElement {
        constructor() {
            super();
            // style the element after a fsa-alert--info
            this.className =
                "fsa-alert fsa-alert--info  fsa-alert--no-icon fsa-m--xxs fsa-m-l--none fsa-m-r--none fsa-p--none";
            this.role = "alert";
            const closeBtn = document.createElement("button");
            closeBtn.className = "fsa-alert__close";
            closeBtn.dataset.behavior = "alert-dismiss";
            closeBtn.type = "button";
            closeBtn.title = "Dismiss this message";
            closeBtn.ariaLabel = "Dismiss this message";
            closeBtn.addEventListener("click", function (event) {
                const alert = closeBtn.closest(".fsa-alert");
                alert.style.opacity = 0;
                alert.remove();
            });
            this.appendChild(closeBtn);

            // Arg Fields
            this.argFields = ["argName", "argDesc", "argExample", "argInternal", "argDefault", "argOptions"];
            this.urlArgs = this.dataset.args;
            // Check for hash or query style (#args or ?args)
            this.hashStyle = false;
            if (Object.keys(this.dataset).includes("hash")) {
                if (parseInt(this.dataset.hash) === 1) {
                    this.hashStyle = true;
                }
            }

            // Check for internal args request
            this.internalVisible = false;
            this.checkForInternalArg();

            // If there are no 'data-args' present, don't show anything
            if (!Object.keys(this.dataset).includes("args")) {
                this.style.display = "none";
            } else {
                this.style.display = "block";
            }

            // base element: <details></details>
            this.details = document.createElement("details");
            this.details.className = "fsa-alert__body fsa-p--none";
            this.appendChild(this.details);

            // summary
            this.summary = document.createElement("summary");
            this.summary.className = "fsa-m-l--xxs";
            this.summary.style.minHeight = "3.7rem";
            this.summary.style.padding = "5px 36px 0px 5px";
            this.summary.innerHTML = /*html*/ `
                <svg 
                    class="fsa-icon fsa-icon--1 fsa-m-r--s" 
                    aria-hidden="true" 
                    focusable="false" 
                    role="img" 
                    xmlns="http://www.w3.org/2000/svg" 
                    width="24" 
                    height="24" 
                    viewBox="0 0 24 24"
                >
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 17h-2v-2h2v2zm2.07-7.75l-.9.92C13.45 12.9 13 13.5 13 15h-2v-.5c0-1.1.45-2.1 1.17-2.83l1.24-1.26c.37-.36.59-.86.59-1.41 0-1.1-.9-2-2-2s-2 .9-2 2H8c0-2.21 1.79-4 4-4s4 1.79 4 4c0 .88-.36 1.68-.93 2.25z"></path>
                </svg>`;
            this.summary.innerHTML +=
                "This page can auto-populate with URL encoded arguments. Click here for more information.";
            this.details.appendChild(this.summary);

            // details-content
            this.content = document.createElement("div");
            this.content.classList.add("details-content");
            this.details.appendChild(this.content);

            let header = document.createElement("h4");
            header.style.marginTop = "1rem";
            header.innerHTML = "Optional URL Parameters";
            this.content.appendChild(header);

            let tableDiv = document.createElement("div");
            tableDiv.style.overflow = "hidden";
            tableDiv.style.overflowY = "scroll";
            tableDiv.style.maxHeight = "500px";
            tableDiv.style.border = "0.5px solid #dbdad9";
            tableDiv.classList.add("fsa-m--none", "fsa-m-b--s");
            this.argTable = document.createElement("table");
            this.argTable.classList.add("fsa-table", "fsa-table--responsive", "fsa-m--none");
            let tblHeader = this.argTable.createTHead();
            let row = tblHeader.insertRow(0);
            let c0 = row.insertCell(0);
            c0.style.fontWeight = "bold";
            c0.innerHTML = "URL Key";
            let c1 = row.insertCell(1);
            c1.innerHTML = "Description";
            c1.style.fontWeight = "bold";
            tableDiv.appendChild(this.argTable);
            this.content.appendChild(tableDiv);
            this.tbody = this.argTable.createTBody();

            this.example = document.createElement("a");
            this.example.target = "_blank";
            this.example.classList.add("fsa-link", "fsa-link--underline-none");
            this.example.innerHTML += /*html*/ `
                <svg 
                    class="fsa-icon fsa-icon--size-1" 
                    aria-hidden="true" 
                    focusable="false" 
                    role="img" 
                    fill="#494440" 
                    xmlns="http://www.w3.org/2000/svg" 
                    width="24" 
                    height="24" 
                    viewBox="0 0 24 24"
                >
                    <path d="M19 19H5V5h7V3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2v-7h-2v7zM14 3v2h3.59l-9.83 9.83 1.41 1.41L19 6.41V10h2V3h-7z"></path>
                </svg>`;
            this.example.innerHTML += "&nbsp;<b>Example Link</b>";
            this.content.appendChild(this.example);

            this.exampleText = document.createElement("pre");
            this.exampleText.classList.add("fsa-m--none", "fsa-color--secondary");
            this.exampleText.style.wordWrap = "break-word";
            this.content.appendChild(this.exampleText);

            window.onhashchange = () => {
                this.checkForInternalArg();
                this.addArguments();
            };
            if (this.dataset.args) {
                this.addArguments();
            }
        }

        checkForInternalArg() {
            let currentWindowArgs = getQueryArgs();
            let currentWindowArgKeys = Object.keys(currentWindowArgs);
            currentWindowArgKeys.forEach((key) => {
                if (`${key}`.toLowerCase() === "internal") {
                    let val = currentWindowArgs[key];
                    if (val === true || val === "true") {
                        this.internalVisible = true;
                    }
                }
            });
        }

        addArguments() {
            // Clear any old args
            this.example.href = window.location.origin + window.location.pathname + (this.hashStyle ? "#" : "?");
            while (this.tbody.firstChild) {
                this.tbody.removeChild(this.tbody.firstChild);
            }

            const argsObject = this.dataset.args ? JSON.parse(this.dataset.args) : [];
            let addedAtLeastOneArg = false;
            this.checkForInternalArg();
            for (let arg of argsObject) {
                if (arg.argName && arg.argDesc && arg.argExample) {
                    arg.argInternal = arg.argInternal ? arg.argInternal : false;
                    arg.argOptions = arg.argOptions ? arg.argOptions : [];
                    arg.argDefault = arg.argDefault ? arg.argDefault : null;
                    if (arg.argInternal && !this.internalVisible) {
                        continue;
                    }
                    this.example.href += (addedAtLeastOneArg ? "&" : "") + arg.argName + "=" + arg.argExample;
                    let row = this.tbody.insertRow(-1);
                    let cell0 = row.insertCell(0);
                    cell0.style.verticalAlign = "middle";
                    cell0.innerHTML = /*html*/ `<span class="fsa-label fsa-label--large fsa-label--neutral details-cell">${arg.argName}</span>`;
                    let cell1 = row.insertCell(1);
                    cell1.style.verticalAlign = "middle";
                    let cellDesc = /*html*/ `<p>${arg.argDesc} <br><b>Example:</b> <span class='fsa-font--mono'><em>${arg.argName}=${arg.argExample}</em></span>`;
                    if (arg.argOptions.length > 0) {
                        cellDesc += /*html*/ `<br><b>Options:</b> <span class='fsa-font--mono'>[${arg.argOptions.join(", ")}]</span>`;
                    }
                    if (arg.argDefault != null) {
                        cellDesc += /*html*/ `<br><b>Default:</b> <span class='fsa-font--mono'>${arg.argDefault}</span>`;
                    }
                    cell1.innerHTML = cellDesc;
                    addedAtLeastOneArg = true;
                }
            }

            this.exampleText.innerHTML = this.example.href;

            if (addedAtLeastOneArg) {
                this.style.display = "block";
            }
        }
    },
);

export function setNwccSelectData(
    id,
    title = null,
    imgUrl = null,
    imgAltText = null,
    btnText = null,
    badgeText = null,
    badgeValue = null,
    helpText = null,
) {
    const nwccSelectElement = document.querySelector(`nwcc-select[data-id="${id}"]`);
    title && nwccSelectElement.setAttribute("data-title", title);
    imgUrl && nwccSelectElement.setAttribute("data-img-url", imgUrl);
    imgAltText && nwccSelectElement.setAttribute("data-img-alt-text", imgAltText);
    btnText && nwccSelectElement.setAttribute("data-btn-text", btnText);
    badgeText && nwccSelectElement.setAttribute("data-badge-text", badgeText);
    badgeValue && nwccSelectElement.setAttribute("data-badge-value", `${badgeValue}`);
    helpText && nwccSelectElement.setAttribute("data-help-text", helpText);
    nwccSelectElement.update();
}

/** 'nwcc-select' is a custom element that templates the structure of a
 * select with optional image, badge and help string
 * Simply place a <nwcc-select></fsa-select>
 * element in a page in order to utilize the setNwccSelectData() function.
 */
customElements.define(
    "nwcc-select",
    class extends HTMLElement {
        getImg(id, dataset) {
            if (dataset.imgUrl && dataset.imgAltText) {
                return /*html*/ `
                    <div class="fsa-level__item--align-middle fsa-box fsa-box--br-3 fsa-m--xs fsa-show@m">
                        <img
                            id="${id}img"
                            class="fsa-level__item--align-middle plot-thumb"
                            src="${dataset.imgUrl}"
                            alt="${dataset.imgAltText}"
                            width
                            height
                        >
                    </div>`;
            } else {
                return "";
            }
        }

        getButton(id, dataset) {
            return dataset.btnText
                ? /*html*/ `<button id="${id}btn" class="fsa-btn fsa-btn--primary" type="submit">${dataset.btnText}</button>`
                : "";
        }

        getBadge(id, dataset) {
            if (dataset.badgeValue || dataset.badgeText) {
                return /*html*/ `
                    <div class="fsa-level">
                        <span class="fsa-badge" id="${id}badge">${dataset.badgeValue || ""}</span>
                        <span class="fsa-field__label-desc">${dataset.badgeText || ""}</span>
                    </div>`;
            }
            return "";
        }

        getSelectId(id) {
            return id ? `${id}-` : "";
        }

        getSelect(dataset) {
            const id = this.getSelectId(dataset.id);
            return /*html*/ `
                <div class="fsa-level">
                    ${this.getImg(id, dataset)}
                    <div class="fsa-field fsa-level__item--align-middle">
                        <div class="fsa-m-l--m">
                            <label class="fsa-field__label" for="${id}dd">
                                ${dataset.title || ""}
                                ${this.getBadge(id, dataset)}
                            </label>
                            <div class="fsa-level">
                                <select
                                    class="fsa-select fsa-field__item"
                                    id="${id}dd"
                                    name="${id}dd"
                                    aria-describedby="${id}dd_help"
                                ></select>
                                ${this.getButton(id, dataset)}
                            </div>
                            <span class="fsa-field__help" id="${id}dd_help">${dataset.helpText || ""}</span>
                        </div>
                    </div>
                </div>`;
        }

        constructor() {
            super();
            if (this.dataset.id) {
                this.innerHTML = this.getSelect(this.dataset);
            }
        }

        update() {
            if (this.dataset.id) {
                this.innerHTML = this.getSelect(this.dataset);
            }
        }
    },
);
